import { useState } from 'react';
import { Trophy } from 'lucide-react';
import { EmptyState } from '@/components/ui/empty-state';
import { Skeleton } from '@/components/ui/skeleton';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { useEventStandings } from '@/api/extensions/useScoringOps';
import type { StandingEntry } from '@/api/extensions/useScoringOps';
import { resolveEventScoring, timedScoreDecimals } from '@/constants/event-types';
import { countPrizeParticipants, formatPrizeWithShare } from '@/utils/prize-share';

interface ResultsViewProps {
  events: any[];
}

function rankClass(rank: number): string {
  if (rank === 1) return 'text-yellow-500 font-bold';
  if (rank === 2) return 'text-zinc-400 font-bold';
  if (rank === 3) return 'text-amber-700 font-bold';
  return '';
}

function rankLabel(rank: number): string {
  if (rank === 1) return '1st';
  if (rank === 2) return '2nd';
  if (rank === 3) return '3rd';
  return String(rank);
}

export function ResultsView({ events }: ResultsViewProps) {
  const [selectedEventId, setSelectedEventId] = useState(events[0]?.id ?? '');
  // Scope: null = overall Average (all rounds); a number = that round alone.
  const [scope, setScope] = useState<number | null>(null);

  const { data: standingsResponse, isLoading } = useEventStandings(
    selectedEventId,
    scope != null ? { round: scope } : undefined,
  );

  if (events.length === 0) {
    return (
      <EmptyState
        icon={Trophy}
        title="No events"
        description="No events available for results."
      />
    );
  }

  const rows: StandingEntry[] = (standingsResponse as any)?.data ?? (standingsResponse as unknown as any[]) ?? [];
  // Only surface the prize column when this scope actually pays a prize.
  const hasPrizes = rows.some((s) => (s.prize_amount ?? 0) > 0);
  const prizeHeading = scope != null ? `Round ${scope} Prize` : 'Avg Prize';
  // Which scopes carry prizes — drives the Average/Round scope toggle.
  const prizeScopes = (standingsResponse as any)?.prize_scopes as
    | { average: boolean; rounds: number[] }
    | undefined;
  const scopeOptions: (number | null)[] = prizeScopes
    ? [...(prizeScopes.average ? [null] : []), ...prizeScopes.rounds]
    : [];
  const showScopeToggle = scopeOptions.length > 1;
  // Barrel racing times show to the thousandth; other timed events to hundredths.
  const selectedEvent = events.find((e: any) => e.id === selectedEventId) ?? events[0];
  const timeDecimals = timedScoreDecimals(resolveEventScoring(selectedEvent).isBarrelRacing);

  return (
    <div className="space-y-4">
      {/* Event + scope selectors */}
      {(events.length > 1 || showScopeToggle) && (
        <div className="flex flex-wrap items-center gap-2">
          {events.length > 1 && (
            <Select
              value={selectedEventId}
              onValueChange={(v) => {
                setSelectedEventId(v);
                setScope(null);
              }}
            >
              <SelectTrigger className="w-full sm:w-64 text-sm">
                <SelectValue placeholder="Select an event" />
              </SelectTrigger>
              <SelectContent>
                {events.map((ev: any) => (
                  <SelectItem key={ev.id} value={ev.id}>
                    {ev.event_name || `Event ${ev.id.slice(0, 8)}`}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}
          {showScopeToggle && (
            <Select
              value={scope != null ? String(scope) : 'avg'}
              onValueChange={(v) => setScope(v === 'avg' ? null : parseInt(v))}
            >
              <SelectTrigger className="w-full sm:w-48 text-sm">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {scopeOptions.map((s) => (
                  <SelectItem key={s == null ? 'avg' : s} value={s == null ? 'avg' : String(s)}>
                    {s == null ? 'Average (All Rounds)' : `Round ${s}`}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}
        </div>
      )}

      {isLoading ? (
        <div className="space-y-2">
          {Array.from({ length: 6 }).map((_, i) => (
            <Skeleton key={i} className="h-10 w-full" />
          ))}
        </div>
      ) : rows.length === 0 ? (
        <EmptyState
          icon={Trophy}
          title="No results available yet"
          description="Results will appear once scoring has been completed for this event."
        />
      ) : (
        <div className="overflow-x-auto rounded-md border">
          <table className="min-w-full text-sm">
            <thead>
              <tr className="border-b bg-muted/50">
                <th className="px-4 py-2 text-left font-medium">Rank</th>
                <th className="px-4 py-2 text-left font-medium">Team</th>
                <th className="px-4 py-2 text-left font-medium">Header</th>
                <th className="px-4 py-2 text-left font-medium">Heeler</th>
                <th className="px-4 py-2 text-right font-medium">Catches</th>
                <th className="px-4 py-2 text-right font-medium">Total Time</th>
                {hasPrizes && <th className="px-4 py-2 text-right font-medium">{prizeHeading}</th>}
              </tr>
            </thead>
            <tbody>
              {rows.map((s) => (
                <tr key={s.team_id} className="border-b last:border-b-0">
                  <td className={`px-4 py-2 tabular-nums ${rankClass(s.placement)}`}>
                    {rankLabel(s.placement)}
                  </td>
                  <td className="px-4 py-2 tabular-nums text-muted-foreground">
                    #{s.team_number}
                  </td>
                  <td className="px-4 py-2">{s.member_names?.[0] || '—'}</td>
                  <td className="px-4 py-2">{s.member_names?.[1] || '—'}</td>
                  <td className="px-4 py-2 text-right tabular-nums">
                    {s.successful_catches}/{s.total_runs}
                  </td>
                  <td className="px-4 py-2 text-right tabular-nums">
                    {typeof s.total_time === 'number' ? s.total_time.toFixed(timeDecimals) + 's' : '—'}
                  </td>
                  {hasPrizes && (
                    <td className="px-4 py-2 text-right tabular-nums font-semibold text-emerald-600 dark:text-emerald-400">
                      {formatPrizeWithShare(s.prize_amount, countPrizeParticipants(s.member_names))}
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
